import React from 'react'
import { Panel, FormControl, Button } from 'react-bootstrap'
import { I18n } from 'react-redux-i18n'
import Select from 'react-select'
import AgentSearchSelect, { createQuery } from '../agent/AgentSearchSelect'
import ProductSearchSelect from '../product/ProductSearchSelect'
import StationSearchSelect from '../station/StationSearchSelect'
import { checkIfAnyGrantedByGivenAuth } from '../../modules/authService'
import { archivedDataAccess } from '../../modules/accessList'
import DateTime from 'react-datetime'
import moment from 'moment'

export default function ReservationTableFilterPanel({
  isShow,
  filter,
  onFilterChange,
  onClearFilter,
  isJobAssignable,
  auth,
}) {
  const fieldsLabel = I18n.t('reservationPage.fields')

  const startDateValid = (current) => {
    if (filter.serviceDateTimeEnd)
      return current
        .startOf('day')
        .isBefore(filter.serviceDateTimeEnd.endOf('day'))
    return true
  }

  const endDateValid = (current) => {
    if (filter.serviceDateTimeStart)
      return current
        .endOf('day')
        .isAfter(filter.serviceDateTimeStart.startOf('day'))
    return true
  }

  return (
    <Panel collapsible expanded={isShow}>
      <div className="p-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left Column - Main Pax, Confirmation, Product, Station */}
          <div className="lg:col-span-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <FormControl
                  type="text"
                  value={filter.mainPaxName || ''}
                  onChange={(e) =>
                    onFilterChange('mainPaxName', e.currentTarget.value)
                  }
                  placeholder={fieldsLabel.mainPax.name}
                />
              </div>
              <div>
                <FormControl
                  type="text"
                  value={filter.confirmationNumber || ''}
                  onChange={(e) =>
                    onFilterChange('confirmationNumber', e.currentTarget.value)
                  }
                  placeholder={fieldsLabel.confirmationNumber}
                />
              </div>
            </div>
            <div className="mb-4">
              <ProductSearchSelect
                value={filter.productId}
                onChange={(value) => onFilterChange('productId', value)}
                placeholder={fieldsLabel.product}
                selectValue={(product) => product._id}
                initialValue={filter.productId}
              />
            </div>
            <div>
              <StationSearchSelect
                initialValue={filter.stationId}
                value={filter.stationId}
                onChange={(value) => onFilterChange('stationId', value)}
                selectValue={(station) => station._id}
                placeholder={fieldsLabel.station}
              />
            </div>
          </div>

          {/* Middle Column - Flight Number, Agent */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div>
              <FormControl
                type="text"
                value={filter.flightNumber || ''}
                onChange={(e) =>
                  onFilterChange('flightNumber', e.currentTarget.value)
                }
                placeholder={fieldsLabel.flightNumber}
              />
            </div>
            <div>
              <AgentSearchSelect
                initialValue={filter.agentId}
                value={filter.agentId}
                onChange={(value) => onFilterChange('agentId', value)}
                createQuery={(search) => ({
                  ...createQuery(search),
                  _showDeleted: true,
                })}
                selectValue={(agent) => agent._id}
                placeholder={fieldsLabel.agent}
              />
            </div>
          </div>

          {/* Status Column */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div>
              <Select
                value={filter.status}
                onChange={(value) => onFilterChange('status', value)}
                placeholder={fieldsLabel.status}
                simpleValue
                options={statusOptions}
              />
            </div>
            {isJobAssignable === undefined && (
              <div>
                <Select
                  value={filter.jobAssignStatus}
                  onChange={(value) => onFilterChange('jobAssignStatus', value)}
                  placeholder={fieldsLabel.jobAssignStatus}
                  simpleValue
                  options={jobAssignStatusOptions}
                />
              </div>
            )}
          </div>

          {/* Date Range Column */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div>
              <DateTime
                value={filter.serviceDateTimeStart}
                onChange={(value) => {
                  if (value && !moment.isMoment(value)) return
                  onFilterChange('serviceDateTimeStart', value)
                }}
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                isValidDate={startDateValid}
                inputProps={{
                  placeholder: fieldsLabel.serviceDateTimeStart,
                }}
              />
            </div>
            <div>
              <DateTime
                value={filter.serviceDateTimeEnd}
                onChange={(value) => {
                  if (value && !moment.isMoment(value)) return
                  onFilterChange('serviceDateTimeEnd', value)
                }}
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                isValidDate={endDateValid}
                inputProps={{ placeholder: fieldsLabel.serviceDateTimeEnd }}
              />
            </div>
          </div>

          {/* Actions Column */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div>
              <Button className="w-full" onClick={onClearFilter}>
                <span className="glyphicon glyphicon-trash" /> Clear Filter
              </Button>
            </div>
            {auth && checkIfAnyGrantedByGivenAuth(auth, archivedDataAccess) && (
              <div className="flex justify-center items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filter.searchArchived || false}
                    onChange={(e) =>
                      onFilterChange('searchArchived', e.target.checked)
                    }
                  />
                  <span>Search Archived</span>
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
    </Panel>
  )
}

const statusOptions = [
  { label: 'TENTATIVE', value: 'tentative' },
  { label: 'CONFIRMED', value: 'confirmed' },
  { label: 'COMPLETED', value: 'completed' },
  { label: 'NOSHOW', value: 'noShow' },
  { label: 'MISSEDCASE', value: 'missedCase' },
  { label: 'CANCELED', value: 'canceled' },
]

const jobAssignStatusOptions = [
  { label: 'NO REQUIRED', value: 'noRequired' },
  { label: 'INCOMPLETE', value: 'inComplete' },
  { label: 'COMPLETED', value: 'completed' },
]
